import { useState } from 'react'
import { Download } from 'lucide-react'
import type { Message } from '../types'
import { avatarColor, initials, renderBody } from '../mentions'
import { fileIcon, formatBytes, isImage } from '../attachments'
import { ImageLightbox } from '@/features/tasks/components/files/ImageLightbox'

function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleString('pl-PL')
}

// Okno edycji = backendowy collab.edit-window-minutes (domyślnie 10). Po nim edycja daje 409,
// więc chowamy "Edytuj". Polling listy (7s) odświeża widoczność. Usuwanie nie ma okna.
const EDIT_WINDOW_MIN = 10

function withinEditWindow(createdAtIso: string): boolean {
  return Date.now() - new Date(createdAtIso).getTime() < EDIT_WINDOW_MIN * 60_000
}

export function MessageItem({ message, currentUserId, names, onEdit, onDelete }: {
  message: Message
  currentUserId: number
  /** Mapa id -> fullName (autorzy + wzmianki), z `useUserNames`. */
  names: Map<number, string>
  onEdit: (id: number) => void
  onDelete: (id: number) => void
}) {
  const author = names.get(message.authorUserId) || `Użytkownik #${message.authorUserId}`
  const isOwn = message.authorUserId === currentUserId
  const canEdit = isOwn && withinEditWindow(message.createdAt)
  const [lightbox, setLightbox] = useState<{ url: string; filename: string } | null>(null)
  const mentionNames = message.mentions
    .map(id => names.get(id))
    .filter((n): n is string => Boolean(n))

  return (
    <div className="group flex gap-3 px-3 py-2 hover:bg-slate-50">
      <div
        className={[
          'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white',
          avatarColor(message.authorUserId),
        ].join(' ')}
        aria-hidden="true"
      >
        {initials(author)}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="font-medium text-slate-800">{author}</span>
          <time className="text-xs text-slate-400" dateTime={message.createdAt}>
            {formatTimestamp(message.createdAt)}
          </time>
          {message.editedAt && !message.deleted && (
            <span className="text-xs text-slate-400">(edytowano)</span>
          )}
        </div>

        {message.deleted ? (
          <p className="mt-0.5 italic text-slate-400">wiadomość usunięta</p>
        ) : (
          <>
            <p className="mt-0.5 whitespace-pre-wrap break-words text-slate-700">
              {renderBody(message.body ?? '', mentionNames)}
            </p>

            {message.attachments && message.attachments.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-2">
                {message.attachments.map(att =>
                  isImage(att.contentType) ? (
                    <button
                      key={att.id}
                      type="button"
                      onClick={() => setLightbox({ url: att.url, filename: att.filename })}
                      aria-label={att.filename}
                      className="block cursor-zoom-in"
                    >
                      <img
                        src={att.url}
                        alt={att.filename}
                        className="max-h-48 rounded-md border border-slate-200 object-cover"
                      />
                    </button>
                  ) : (
                    <a
                      key={att.id}
                      href={att.url}
                      target="_blank"
                      rel="noreferrer"
                      className="group/att inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs hover:bg-slate-50"
                    >
                      {(() => {
                        const Icon = fileIcon(att.contentType)
                        return <Icon className="h-4 w-4 shrink-0 text-slate-400" />
                      })()}
                      <span className="max-w-[14rem] truncate font-medium text-slate-700">{att.filename}</span>
                      <span className="text-slate-400">{formatBytes(att.sizeBytes)}</span>
                      <Download className="h-3.5 w-3.5 text-slate-400 opacity-0 transition-opacity group-hover/att:opacity-100" />
                    </a>
                  ),
                )}
              </div>
            )}
            {isOwn && (
              <div className="mt-1 flex gap-3 text-xs opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => onEdit(message.id)}
                    className="text-sky-600 hover:underline"
                  >
                    Edytuj
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => onDelete(message.id)}
                  className="text-rose-600 hover:underline"
                >
                  Usuń
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {lightbox && (
        <ImageLightbox
          src={lightbox.url}
          filename={lightbox.filename}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  )
}
